import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Typography,
  Drawer,
  Descriptions,
  Empty,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  SearchOutlined,
  MessageOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { botAPI } from '../services/api';
import { MessageLog, ChatInfo, MessageStatistics } from '../types';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const BotMessages: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const botId = parseInt(id || '0');

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<MessageLog[]>([]);
  const [chats, setChats] = useState<ChatInfo[]>([]);
  const [statistics, setStatistics] = useState<MessageStatistics | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedMessage, setSelectedMessage] = useState<MessageLog | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [form] = Form.useForm();

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const response = await botAPI.getMessageStatistics(botId);
      setStatistics(response.data);
    } catch (error: any) {
      console.error('获取统计数据失败:', error);
    }
  };

  // 获取聊天列表
  const fetchChats = async () => {
    try {
      const response = await botAPI.getChats(botId);
      setChats(response.data);
    } catch (error: any) {
      console.error('获取聊天列表失败:', error);
    }
  };

  // 获取消息列表
  const fetchMessages = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const params: any = {
        page,
        pageSize,
        chatId: values.chatId,
        chatType: values.chatType,
        messageType: values.messageType,
        keyword: values.keyword,
      };

      // 处理日期范围
      if (values.dateRange && values.dateRange.length === 2) {
        params.startDate = values.dateRange[0].format('YYYY-MM-DD');
        params.endDate = values.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await botAPI.getMessages(botId, params);
      setMessages(response.data.data);
      setPagination({
        current: response.data.page,
        pageSize: response.data.pageSize,
        total: response.data.total,
      });
    } catch (error: any) {
      message.error('获取消息列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchChats();
    fetchMessages();
  }, []);

  const handleSearch = () => {
    fetchMessages(1, pagination.pageSize);
  };

  const handleReset = () => {
    form.resetFields();
    fetchMessages(1, pagination.pageSize);
  };

  const handleTableChange = (paginationConfig: any) => {
    fetchMessages(paginationConfig.current, paginationConfig.pageSize);
  };

  const handleViewDetail = (record: MessageLog) => {
    setSelectedMessage(record);
    setDrawerVisible(true);
  };

  const getChatTypeBadge = (chatType: string) => {
    const config: Record<string, { color: string; text: string }> = {
      private: { color: 'blue', text: '私聊' },
      group: { color: 'green', text: '群组' },
      supergroup: { color: 'purple', text: '超级群组' },
      channel: { color: 'orange', text: '频道' },
    };
    const { color, text } = config[chatType] || { color: 'default', text: chatType };
    return <Tag color={color}>{text}</Tag>;
  };

  const getMessageTypeBadge = (messageType: string) => {
    const config: Record<string, { color: string; text: string }> = {
      text: { color: 'default', text: '文本' },
      photo: { color: 'blue', text: '图片' },
      video: { color: 'purple', text: '视频' },
      document: { color: 'green', text: '文档' },
      audio: { color: 'orange', text: '音频' },
      voice: { color: 'cyan', text: '语音' },
      sticker: { color: 'magenta', text: '贴纸' },
      location: { color: 'red', text: '位置' },
      contact: { color: 'gold', text: '联系人' },
    };
    const { color, text } = config[messageType] || { color: 'default', text: messageType };
    return <Tag color={color}>{text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '聊天',
      dataIndex: 'chat_title',
      width: 150,
      render: (title: string, record: MessageLog) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title || `Chat ${record.chat_id}`}</Text>
          {getChatTypeBadge(record.chat_type)}
        </Space>
      ),
    },
    {
      title: '发送者',
      dataIndex: 'from_username',
      width: 150,
      render: (username: string, record: MessageLog) => (
        <Space direction="vertical" size={0}>
          <Text>{record.from_first_name || '-'}</Text>
          {username && <Text type="secondary">@{username}</Text>}
        </Space>
      ),
    },
    {
      title: '消息类型',
      dataIndex: 'message_type',
      width: 100,
      render: (type: string) => getMessageTypeBadge(type),
    },
    {
      title: '内容',
      dataIndex: 'text',
      ellipsis: true,
      render: (text: string, record: MessageLog) => {
        const content = text || record.caption || '-';
        return (
          <Text ellipsis={{ tooltip: content }} style={{ maxWidth: 300 }}>
            {content}
          </Text>
        );
      },
    },
    {
      title: '时间',
      dataIndex: 'create_time',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 100,
      render: (_: any, record: MessageLog) => (
        <Button size="small" onClick={() => handleViewDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/bots')}>
            返回
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            机器人消息日志
          </Title>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => fetchMessages()}>
          刷新
        </Button>
      </div>

      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总消息数"
                value={statistics.total_messages}
                prefix={<MessageOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="聊天数"
                value={statistics.total_chats}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="私聊消息"
                value={statistics.private_messages}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="群组消息"
                value={statistics.group_messages}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="chatId" style={{ width: 200 }}>
            <Select placeholder="选择聊天" allowClear showSearch>
              {chats.map((chat) => (
                <Option key={chat.chat_id} value={chat.chat_id}>
                  {chat.chat_title || `Chat ${chat.chat_id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="chatType" style={{ width: 150 }}>
            <Select placeholder="聊天类型" allowClear>
              <Option value="private">私聊</Option>
              <Option value="group">群组</Option>
              <Option value="supergroup">超级群组</Option>
              <Option value="channel">频道</Option>
            </Select>
          </Form.Item>

          <Form.Item name="messageType" style={{ width: 150 }}>
            <Select placeholder="消息类型" allowClear>
              <Option value="text">文本</Option>
              <Option value="photo">图片</Option>
              <Option value="video">视频</Option>
              <Option value="document">文档</Option>
              <Option value="audio">音频</Option>
              <Option value="voice">语音</Option>
            </Select>
          </Form.Item>

          <Form.Item name="keyword" style={{ width: 200 }}>
            <Input placeholder="搜索关键词" allowClear />
          </Form.Item>

          <Form.Item name="dateRange">
            <RangePicker />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 消息列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={messages}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>

      {/* 消息详情抽屉 */}
      <Drawer
        title="消息详情"
        placement="right"
        width={600}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedMessage ? (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="消息 ID">
                {selectedMessage.message_id}
              </Descriptions.Item>
              <Descriptions.Item label="聊天 ID">
                {selectedMessage.chat_id}
              </Descriptions.Item>
              <Descriptions.Item label="聊天标题">
                {selectedMessage.chat_title || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="聊天类型">
                {getChatTypeBadge(selectedMessage.chat_type)}
              </Descriptions.Item>
              <Descriptions.Item label="发送者 ID">
                {selectedMessage.from_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="发送者用户名">
                {selectedMessage.from_username ? `@${selectedMessage.from_username}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="发送者名称">
                {selectedMessage.from_first_name || '-'}{' '}
                {selectedMessage.from_last_name || ''}
              </Descriptions.Item>
              <Descriptions.Item label="消息类型">
                {getMessageTypeBadge(selectedMessage.message_type)}
              </Descriptions.Item>
              <Descriptions.Item label="文本内容">
                {selectedMessage.text || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="图片/视频说明">
                {selectedMessage.caption || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(selectedMessage.create_time).toLocaleString('zh-CN')}
              </Descriptions.Item>
            </Descriptions>

            {selectedMessage.raw_data && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>原始数据</Title>
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(selectedMessage.raw_data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <Empty description="未选择消息" />
        )}
      </Drawer>
    </div>
  );
};

export default BotMessages;
